import React, { useState, useEffect, useRef, ReactNode, useMemo } from "react";
import classNames from "classnames";

import useValidate from "../../validations/useValidate";

import Styles from "./Input.module.scss";
import validations, { numbersOnly } from "../../validations/validations";

type InputProps = {
  id?: string;
  name?: string;
  type?: "text" | "number" | "email" | "tel" | "password" | "hidden" | "date";
  value: string | number;
  placeholder?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onValidate?: (error: string | undefined) => void;
  onClick?: () => void;
  // onBlur?: ((val?: React.FocusEvent<HTMLInputElement>) => void) | (() => void);
  onBlur?: (val: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (val: React.FocusEvent<HTMLInputElement>) => void;
  label?: React.ReactNode;
  labelClass?: string;
  labelPosition?: "left" | "right";
  fullWidth?: boolean;
  validate?:
    | ((value: unknown) => string | undefined | Promise<string | undefined>)
    | Array<(value: unknown) => (string | undefined) | Promise<string | undefined>>
    | ((value: string) => string | undefined)
    | Array<(value: string) => string | undefined>;
  showValidation?: boolean;
  // eslint-disable-next-line
  error?: boolean | string | Promise<any | undefined>;
  errorMessage?: string;
  errorMessageBoxAbsolute?: boolean;
  className?: string;
  titleClass?: string;
  disabled?: boolean;
  greyed?: boolean;
  autoComplete?: string;
  maxLength?: number;
  afterErrorBlurEvent?: () => void;
  // eslint-disable-next-line
  handleKeyPress?: ((value: any) => void) | (() => void);
  reference?: React.RefObject<HTMLInputElement>;
  numeric?: boolean;
  title?: string | ReactNode;
  autoFocus?: boolean;
  required?: boolean;
  inputmode?: "text" | "email" | "tel" | "numeric" | "search" | "none" | "url" | "decimal";
  varient?: "old" | "new";
  "aria-label"?: string;
};

const Input = ({
  id,
  name,
  type = "text",
  placeholder,
  value,
  onChange,
  onValidate,
  label,
  labelPosition,
  labelClass,
  titleClass,
  fullWidth,
  validate,
  showValidation,
  error,
  errorMessage,
  className,
  disabled,
  greyed,
  autoComplete = "",
  maxLength = 50,
  onClick,
  onBlur,
  onFocus,
  afterErrorBlurEvent,
  handleKeyPress,
  reference,
  numeric,
  title,
  autoFocus,
  required,
  inputmode,
  varient = "old",
  "aria-label": ariaLabel,
}: InputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const refError = useRef<string | undefined>();
  const fallbackRef = useRef<HTMLInputElement>(null);
  const errorId = id ? `${id}-error` : `input-error-${name || Math.random().toString(36).substr(2, 9)}`;

  const _validations = useMemo(() => {
    const arr = validate ? (Array.isArray(validate) ? validate : [validate]) : [];
    if (required) {
      arr.push(validations.required);
    }

    const isValueRequired = arr.includes(validations.required);
    // Only run validations if there is a value or if field is required
    return arr?.length > 0 && (value || isValueRequired) ? arr : undefined;
  }, [validate, required, value]);
  //@ts-expect-error validation needs to be typed properly
  const currentError = useValidate(value, _validations, errorMessage);

  useEffect(() => {
    if (autoFocus) {
      reference?.current?.focus() || fallbackRef?.current?.focus();
    }
  }, [autoFocus, reference]);

  useEffect(() => {
    if (onValidate && refError.current !== currentError) {
      refError.current = currentError;
      onValidate(currentError);
    }
  }, [currentError, onValidate]);

  // Intentionally excluding showValidationError from deps to prevent infinite update loop
  useEffect(() => {
    if (showValidation && !showValidationError) {
      setShowValidationError(true);
    } else if (!showValidation && showValidationError) {
      setShowValidationError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showValidation]);

  useEffect(() => {
    // Clear validation error when input becomes empty (unless required)
    if (!value && !required && showValidationError) {
      setShowValidationError(false);
      if (onValidate) {
        onValidate(undefined);
      }
    }
  }, [value, required, showValidationError, onValidate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let allowValueChange = true;
    if (numeric) {
      if (numbersOnly(e.target.value)) {
        allowValueChange = false;
      }
    }
    allowValueChange && onChange(e);
  };

  const titleClasses = classNames({
    [Styles.floatAbove]: isFocused || value?.toString().length > 0,
  });

  const inputClasses = classNames(Styles.row, {
    [Styles.rowFocused]: isFocused,
    [Styles.error]: (errorMessage || currentError) && showValidationError,
    [Styles.disabled]: disabled,
    [Styles.greyed]: greyed,
    [Styles.withLabel]: label,
  });

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);

    if (onBlur) {
      onBlur(e);
    }

    // Only show validation error if there's a value or if field is required
    if ((currentError || errorMessage) && !showValidationError && (value || required)) {
      setShowValidationError(true);
      afterErrorBlurEvent && afterErrorBlurEvent();
    }
  };

  const hasError = ((errorMessage || currentError) && showValidationError) || (error && errorMessage);
  const ariaDescribedBy = hasError ? errorId : undefined;

  return varient === "old" ? (
    <div className={classNames(Styles.formField, className, { [Styles.fullWidth]: fullWidth })}>
      <div className={classNames(Styles.inputWrapper)}>
        {title &&
          (React.isValidElement(title) ? title : <span className={classNames(Styles.title, titleClass)}>{title}</span>)}
        <div
          className={classNames(
            Styles.row,
            { [Styles.greyed]: greyed },
            labelPosition ? Styles[labelPosition] : Styles.right,
            {
              [Styles.withLabel]: label,
              [Styles.focus]: isFocused,
              [Styles.error]: (error || currentError) && showValidationError,
              [Styles.disabled]: disabled,
            }
          )}
        >
          <input
            id={id}
            ref={reference || fallbackRef}
            role="textbox"
            aria-invalid={!!errorMessage || !!currentError}
            aria-describedby={ariaDescribedBy}
            aria-label={ariaLabel}
            type={type}
            name={name}
            maxLength={maxLength}
            placeholder={placeholder || (title && typeof title === "string" ? title : undefined)}
            value={value}
            onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
              setIsFocused(true);
              onFocus && onFocus(e);
            }}
            onChange={handleChange}
            disabled={disabled}
            autoComplete={autoComplete}
            onClick={() => onClick && onClick()}
            onBlur={handleBlur}
            onKeyPress={handleKeyPress}
            inputMode={inputmode || numeric ? "numeric" : undefined}
          />
          {label && <div className={classNames(Styles.label, labelClass)}>{label}</div>}
        </div>
      </div>
      {!disabled && (errorMessage || currentError) && showValidationError && (
        <div id={errorId} className={Styles.errorMessage} role={"alert"}>
          {errorMessage || currentError}
        </div>
      )}
    </div>
  ) : (
    <div className={Styles.newInputWrapper}>
      <div
        className={classNames(Styles.formField, className, {
          [Styles.fullWidth]: fullWidth,
          [Styles.error]: currentError,
        })}
      >
        <div className={Styles.inputWrapper}>
          <div className={inputClasses}>
            {label &&
              (React.isValidElement(label) ? (
                label
              ) : (
                <span className={classNames(Styles.label, labelClass)}>{label}</span>
              ))}
            {title && (
              <div className={classNames(Styles.labelWraper, titleClasses)}>
                <label htmlFor={id} className={titleClasses}>
                  {title}
                </label>
              </div>
            )}

            <input
              id={id}
              ref={reference || fallbackRef}
              role="textbox"
              aria-invalid={!!errorMessage || !!currentError}
              aria-label={ariaLabel}
              aria-describedby={ariaDescribedBy}
              type={type}
              name={name}
              maxLength={maxLength}
              placeholder={placeholder || (title && typeof title === "string" ? title : undefined)}
              value={value}
              onFocus={() => setIsFocused(true)}
              onChange={handleChange}
              disabled={disabled}
              autoComplete={autoComplete}
              onClick={() => onClick && onClick()}
              onBlur={handleBlur}
              onKeyPress={handleKeyPress}
              inputMode={inputmode || numeric ? "numeric" : undefined}
            />
          </div>
          {(errorMessage || currentError) && showValidationError && (
            <div id={errorId} className={Styles.errorMessage} role={"alert"}>
              {errorMessage || currentError}
            </div>
          )}
          {error && errorMessage && (
            <div id={errorId} className={Styles.errorMessage} role="alert">
              {errorMessage}
            </div>
          )}
          {/* {validationError && (
            <div className={Styles.errorMessage} role="alert">
              {validationError}
            </div>
          )} */}
        </div>
      </div>
    </div>
  );
};

export default Input;
